/**
 * ContactList.jsx — Contact list with search, type/category filters, sort (MUI)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Chip, Stack, Divider,
  Select, MenuItem, IconButton, Tooltip, Collapse, TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import { truncate } from '../../lib/utils';
import { useContacts } from '../../hooks/useContacts';
import { useXP } from '../../hooks/useXP';
import UserAvatar from '../shared/UserAvatar';

/* ── Sort helpers ── */
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'company', label: 'Company' },
  { value: 'dateAdded', label: 'Date Added' },
  { value: 'lastActivity', label: 'Last Activity' },
  { value: 'xp', label: 'Total XP' },
];

const GROUP_OPTIONS = [
  { value: 'none', label: 'No Grouping' },
  { value: 'type', label: 'By Type' },
  { value: 'company', label: 'By Company' },
  { value: 'tier', label: 'By Tier' },
];

const TYPE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'no-prospect', label: 'Clients / Crew / Vendors' },
  { value: 'client', label: 'Clients' },
  { value: 'crew', label: 'Crew' },
  { value: 'vendor', label: 'Vendors' },
  { value: 'prospect', label: 'Prospects' },
];

const LS_SORT_KEY = 'rfos_contactlist_sort';
const LS_DIR_KEY  = 'rfos_contactlist_sortdir';

function getLatestInteraction(contact) {
  const ix = contact.data?.interactions;
  if (!ix?.length) return '';
  return ix.reduce((latest, i) => (i.date > latest ? i.date : latest), '');
}

/* ── Tier from XP (mirrors useXP thresholds) ── */
function getTierFromXP(totalXP) {
  if (totalXP >= 2000) return 'platinum';
  if (totalXP >= 500) return 'gold';
  if (totalXP >= 100) return 'silver';
  return 'bronze';
}

const TIER_COLORS = { bronze: '#B06820', silver: '#8B8B8B', gold: '#C9A500', platinum: '#6B7280' };

export default function ContactList({ selectedContactId, onSelect, onNewContact }) {
  const { data: allContacts = [], isLoading, error } = useContacts();
  const { getContactXP } = useXP();

  /* ── State ── */
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem(LS_SORT_KEY) || 'xp'; } catch { return 'xp'; }
  });
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem(LS_DIR_KEY) || 'desc'; } catch { return 'desc'; }
  });
  const [showFilters, setShowFilters] = useState(false);
  const [groupBy, setGroupBy] = useState('none');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Persist sort preferences
  useEffect(() => {
    try { localStorage.setItem(LS_SORT_KEY, sortBy); } catch { /* noop */ }
  }, [sortBy]);
  useEffect(() => {
    try { localStorage.setItem(LS_DIR_KEY, sortDir); } catch { /* noop */ }
  }, [sortDir]);

  // Phase 2 category filters
  const [tierFilter, setTierFilter] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [payTypeFilter, setPayTypeFilter] = useState([]);
  const [rosterFilter, setRosterFilter] = useState(null); // null = off, true = in roster

  /* ── Derived: unique tags & roles across all contacts ── */
  const { allTags, allRoles } = useMemo(() => {
    const tagSet = new Set();
    const roleSet = new Set();
    for (const c of allContacts) {
      (c.data?.tags || []).forEach((t) => tagSet.add(t));
      if (c.data?.primaryRole) roleSet.add(c.data.primaryRole);
    }
    return { allTags: [...tagSet].sort(), allRoles: [...roleSet].sort() };
  }, [allContacts]);

  /* ── XP lookup map (memoised) ── */
  const xpMap = useMemo(() => {
    const m = new Map();
    for (const c of allContacts) m.set(c.id, getContactXP(c));
    return m;
  }, [allContacts, getContactXP]);

  /* ── Active filter count ── */
  const activeFilterCount = (tierFilter.length > 0 ? 1 : 0) + (tagFilter.length > 0 ? 1 : 0)
    + (roleFilter.length > 0 ? 1 : 0) + (payTypeFilter.length > 0 ? 1 : 0) + (rosterFilter !== null ? 1 : 0);

  // Reset page when any filter changes
  useEffect(() => { setPage(0); }, [search, typeFilter, tierFilter, tagFilter, roleFilter, payTypeFilter, rosterFilter]);

  /* ── Filter + Sort pipeline ── */
  const filteredContacts = useMemo(() => {
    let result = allContacts;

    // Type filter
    if (typeFilter === 'no-prospect') result = result.filter((c) => c.type !== 'prospect');
    else if (typeFilter !== 'All') result = result.filter((c) => c.type === typeFilter);

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) =>
        (c.name || '').toLowerCase().includes(q)
        || (c.company || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
      );
    }

    // Tier filter
    if (tierFilter.length > 0) {
      result = result.filter((c) => {
        const xp = xpMap.get(c.id);
        return xp && tierFilter.includes(getTierFromXP(xp.totalXP));
      });
    }

    // Tag filter (contact has ANY of the selected tags)
    if (tagFilter.length > 0) {
      result = result.filter((c) => {
        const tags = c.data?.tags || [];
        return tagFilter.some((t) => tags.includes(t));
      });
    }

    // Role filter (crew only)
    if (roleFilter.length > 0) {
      result = result.filter((c) => c.data?.primaryRole && roleFilter.includes(c.data.primaryRole));
    }

    // Pay type filter (crew only)
    if (payTypeFilter.length > 0) {
      result = result.filter((c) => c.data?.payType && payTypeFilter.includes(c.data.payType));
    }

    // Roster filter
    if (rosterFilter !== null) {
      result = result.filter((c) => !!c.data?.inRoster === rosterFilter);
    }

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    result = [...result].sort((a, b) => {
      // Incomplete contacts always float to top
      const aInc = a.data?.incomplete ? 1 : 0;
      const bInc = b.data?.incomplete ? 1 : 0;
      if (aInc !== bInc) return bInc - aInc;

      let cmp = 0;
      switch (sortBy) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'company': cmp = (a.company || '').localeCompare(b.company || ''); break;
        case 'dateAdded': {
          const da = a.createdAt || '';
          const db = b.createdAt || '';
          cmp = da < db ? -1 : da > db ? 1 : 0;
          break;
        }
        case 'lastActivity': {
          const la = getLatestInteraction(a);
          const lb = getLatestInteraction(b);
          cmp = la < lb ? -1 : la > lb ? 1 : 0;
          break;
        }
        case 'xp': {
          const xa = xpMap.get(a.id)?.totalXP || 0;
          const xb = xpMap.get(b.id)?.totalXP || 0;
          cmp = xa - xb;
          break;
        }
      }
      if (cmp !== 0) return dir * cmp;
      // Tiebreaker: name ascending
      return (a.name || '').localeCompare(b.name || '');
    });

    return result;
  }, [allContacts, search, typeFilter, sortBy, sortDir, tierFilter, tagFilter, roleFilter, payTypeFilter, rosterFilter, xpMap]);

  /* ── Grouped contacts (Phase 3) ── */
  const groupedContacts = useMemo(() => {
    if (groupBy === 'none') return null;
    const groups = new Map();
    const TYPE_ORDER = ['client', 'crew', 'vendor', 'prospect'];
    const TIER_ORDER = ['platinum', 'gold', 'silver', 'bronze'];
    filteredContacts.forEach((c) => {
      let key;
      switch (groupBy) {
        case 'type': key = c.type || 'unknown'; break;
        case 'company': key = c.company || '(No Company)'; break;
        case 'tier': {
          const xp = xpMap.get(c.id);
          key = xp ? getTierFromXP(xp.totalXP) : 'bronze';
          break;
        }
        default: key = 'other';
      }
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
    // Sort group keys
    let sortedKeys = [...groups.keys()];
    if (groupBy === 'type') sortedKeys.sort((a, b) => (TYPE_ORDER.indexOf(a) === -1 ? 99 : TYPE_ORDER.indexOf(a)) - (TYPE_ORDER.indexOf(b) === -1 ? 99 : TYPE_ORDER.indexOf(b)));
    else if (groupBy === 'tier') sortedKeys.sort((a, b) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b));
    else sortedKeys.sort((a, b) => a.localeCompare(b));
    return sortedKeys.map((k) => ({ label: groupBy === 'type' ? (k.charAt(0).toUpperCase() + k.slice(1) + 's') : groupBy === 'tier' ? (k.charAt(0).toUpperCase() + k.slice(1)) : k, contacts: groups.get(k) }));
  }, [filteredContacts, groupBy, xpMap]);

  /* ── Chip toggle helper ── */
  const toggleChip = useCallback((arr, setArr, val) => {
    setArr((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  }, []);

  const clearAllFilters = () => { setTierFilter([]); setTagFilter([]); setRoleFilter([]); setPayTypeFilter([]); setRosterFilter(null); };

  if (error) return <Paper sx={{ p: 2 }}><Typography color="error" variant="body2">Failed to load contacts: {error.message}</Typography></Paper>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Contacts</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onNewContact} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>New</Button>
      </Box>

      {/* ── Search ── */}
      <TextField size="small" fullWidth placeholder="Search name, company, email..." value={search} onChange={(e) => setSearch(e.target.value)} />

      {/* ── Type dropdown + Sort dropdown + direction + Filters (unified toolbar) ── */}
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Select
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ fontSize: '0.7rem', minWidth: 80, fontFamily: '"DM Mono", monospace' }}
          renderValue={(v) => TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v}
        >
          {TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem' }}>{o.label}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ fontSize: '0.7rem', minWidth: 100, fontFamily: '"DM Mono", monospace' }}
          renderValue={(v) => `Sort: ${SORT_OPTIONS.find((o) => o.value === v)?.label ?? v}`}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem' }}>{o.label}</MenuItem>
          ))}
        </Select>
        <Tooltip title={sortDir === 'asc' ? 'Ascending' : 'Descending'}>
          <IconButton size="small" onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')} sx={{ p: { xs: 1, md: 0.25 } }}>
            {sortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Select
          size="small"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          sx={{ fontSize: '0.7rem', minWidth: 90, fontFamily: '"DM Mono", monospace' }}
          renderValue={(v) => v === 'none' ? 'Group' : `Group: ${GROUP_OPTIONS.find((o) => o.value === v)?.label?.replace('By ', '') ?? v}`}
        >
          {GROUP_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.75rem' }}>{o.label}</MenuItem>
          ))}
        </Select>
        <Tooltip title="Category filters">
          <IconButton size="small" onClick={() => setShowFilters((v) => !v)} color={activeFilterCount > 0 ? 'primary' : 'default'} sx={{ p: { xs: 1, md: 0.25 } }}>
            <FilterListIcon sx={{ fontSize: 16 }} />
            {activeFilterCount > 0 && (
              <Typography component="span" sx={{ fontSize: '0.55rem', fontWeight: 700, position: 'absolute', top: 2, right: 2, bgcolor: 'primary.main', color: '#fff', borderRadius: '50%', width: 14, height: 14, lineHeight: '14px', textAlign: 'center' }}>
                {activeFilterCount}
              </Typography>
            )}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Category Filters (collapsible) ── */}
      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Tier */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.25, display: 'block' }}>Tier</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {['bronze', 'silver', 'gold', 'platinum'].map((tier) => (
                <Chip key={tier} label={tier.charAt(0).toUpperCase() + tier.slice(1)} size="small"
                  onClick={() => toggleChip(tierFilter, setTierFilter, tier)}
                  sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 }, bgcolor: tierFilter.includes(tier) ? TIER_COLORS[tier] : undefined, color: tierFilter.includes(tier) ? '#fff' : undefined }}
                  variant={tierFilter.includes(tier) ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>

          {/* Tags */}
          {allTags.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.25, display: 'block' }}>Tags</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxHeight: 60, overflowY: 'auto' }}>
                {allTags.map((tag) => (
                  <Chip key={tag} label={truncate(tag, 18)} size="small"
                    onClick={() => toggleChip(tagFilter, setTagFilter, tag)}
                    color={tagFilter.includes(tag) ? 'primary' : 'default'}
                    variant={tagFilter.includes(tag) ? 'filled' : 'outlined'}
                    sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 } }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Role (crew-relevant) */}
          {allRoles.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.25, display: 'block' }}>Role</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {allRoles.map((role) => (
                  <Chip key={role} label={role} size="small"
                    onClick={() => toggleChip(roleFilter, setRoleFilter, role)}
                    color={roleFilter.includes(role) ? 'secondary' : 'default'}
                    variant={roleFilter.includes(role) ? 'filled' : 'outlined'}
                    sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 } }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Pay Type */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.25, display: 'block' }}>Pay Type</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {['percentage', 'hourly', 'flat'].map((pt) => (
                <Chip key={pt} label={pt.charAt(0).toUpperCase() + pt.slice(1)} size="small"
                  onClick={() => toggleChip(payTypeFilter, setPayTypeFilter, pt)}
                  color={payTypeFilter.includes(pt) ? 'info' : 'default'}
                  variant={payTypeFilter.includes(pt) ? 'filled' : 'outlined'}
                  sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 } }}
                />
              ))}
            </Stack>
          </Box>

          {/* In Roster */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', mb: 0.25, display: 'block' }}>Roster</Typography>
            <Stack direction="row" spacing={0.5}>
              <Chip label="In Roster" size="small"
                onClick={() => setRosterFilter((v) => v === true ? null : true)}
                color={rosterFilter === true ? 'success' : 'default'}
                variant={rosterFilter === true ? 'filled' : 'outlined'}
                sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 } }}
              />
              <Chip label="Not in Roster" size="small"
                onClick={() => setRosterFilter((v) => v === false ? null : false)}
                color={rosterFilter === false ? 'warning' : 'default'}
                variant={rosterFilter === false ? 'filled' : 'outlined'}
                sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 26, md: 22 } }}
              />
            </Stack>
          </Box>

          {/* Clear all */}
          {activeFilterCount > 0 && (
            <Button size="small" onClick={clearAllFilters} sx={{ fontSize: '0.65rem', textTransform: 'none', alignSelf: 'flex-end' }}>
              Clear all filters
            </Button>
          )}
        </Paper>
      </Collapse>

      {/* ── Contact list ── */}
      <Paper sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>Loading contacts...</Typography>
        ) : filteredContacts.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>No contacts found</Typography>
        ) : groupedContacts ? (
          /* ── Grouped rendering ── */
          groupedContacts.map((group) => (
            <Box key={group.label}>
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {group.label} <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400, fontSize: '0.65rem' }}>({group.contacts.length})</Typography>
                </Typography>
              </Box>
              {group.contacts.map((contact, i) => {
                const xp = xpMap.get(contact.id);
                const tier = xp ? getTierFromXP(xp.totalXP) : null;
                const isIncomplete = contact.data?.incomplete;
                return (
                  <Box key={contact.id}>
                    {isIncomplete && (
                      <Box onClick={() => onSelect(contact)} sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(164,123,26,0.08)', borderLeft: 3, borderColor: 'warning.main', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(164,123,26,0.15)' } }}>
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.65rem' }}>⚠ Finish setting up {contact.name}</Typography>
                      </Box>
                    )}
                    <Box onClick={() => onSelect(contact)} sx={{ px: 1.5, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 1, borderLeft: 2, borderColor: isIncomplete ? 'warning.main' : selectedContactId === contact.id ? 'primary.main' : 'transparent', bgcolor: selectedContactId === contact.id ? 'action.selected' : isIncomplete ? 'rgba(164,123,26,0.04)' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <UserAvatar name={contact.name} size="sm" avatarUrl={contact.data?.avatar_url} contactType={contact.type} />
                          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{contact.name || '(Unnamed)'}</Typography>
                          {tier && xp.totalXP > 0 && <Tooltip title={`${xp.totalXP} XP`}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[tier], flexShrink: 0 }} /></Tooltip>}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>{contact.company || 'N/A'} · {contact.type}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{contact.email || '—'}</Typography>
                      </Box>
                      <Chip label={contact.type} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, flexShrink: 0 }} />
                    </Box>
                    {i < group.contacts.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          ))
        ) : (
          /* ── Flat rendering (no grouping) ── */
          filteredContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((contact, i) => {
            const xp = xpMap.get(contact.id);
            const tier = xp ? getTierFromXP(xp.totalXP) : null;
            const isIncomplete = contact.data?.incomplete;
            return (
              <Box key={contact.id}>
                {isIncomplete && (
                  <Box
                    onClick={() => onSelect(contact)}
                    sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(164,123,26,0.08)', borderLeft: 3, borderColor: 'warning.main', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(164,123,26,0.15)' } }}
                  >
                    <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.65rem' }}>⚠ Finish setting up {contact.name}</Typography>
                  </Box>
                )}
                <Box
                  onClick={() => onSelect(contact)}
                  sx={{
                    px: 1.5, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 1,
                    borderLeft: 2, borderColor: isIncomplete ? 'warning.main' : selectedContactId === contact.id ? 'primary.main' : 'transparent',
                    bgcolor: selectedContactId === contact.id ? 'action.selected' : isIncomplete ? 'rgba(164,123,26,0.04)' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <UserAvatar name={contact.name} size="sm" avatarUrl={contact.data?.avatar_url} contactType={contact.type} />
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{contact.name || '(Unnamed)'}</Typography>
                      {tier && xp.totalXP > 0 && (
                        <Tooltip title={`${xp.totalXP} XP`}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLORS[tier], flexShrink: 0 }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap>{contact.company || 'N/A'} · {contact.type}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{contact.email || '—'}</Typography>
                    {contact.data?.tags?.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {contact.data.tags.slice(0, 2).map((tag, idx) => <Chip key={idx} label={truncate(tag, 15)} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, height: { xs: 24, md: 18 } }} />)}
                        {contact.data.tags.length > 2 && <Typography variant="caption" color="text.disabled">+{contact.data.tags.length - 2}</Typography>}
                      </Stack>
                    )}
                  </Box>
                  <Chip label={contact.type} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', md: '0.6rem' }, flexShrink: 0 }} />
                </Box>
                {i < filteredContacts.length - 1 && <Divider />}
              </Box>
            );
          })
        )}
      </Paper>

      {/* ── Pagination (hidden when grouped) ── */}
      {!groupedContacts && (
        <TablePagination
          component="div"
          count={filteredContacts.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          sx={{ '.MuiTablePagination-toolbar': { minHeight: 36, px: 1 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
        />
      )}
    </Box>
  );
}
